import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Pause, Play, Trash2, Send } from 'lucide-react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { cn } from '../../lib/utils';

interface VoiceRecorderProps {
  onRecordingComplete: (blob: Blob, duration: number) => void;
  maxDuration?: number;
  disabled?: boolean;
}

export function VoiceRecorder({
  onRecordingComplete,
  maxDuration = 120,
  disabled,
}: VoiceRecorderProps) {
  const [state, actions] = useVoiceRecorder();

  // Auto-stop at max duration
  useEffect(() => {
    if (state.duration >= maxDuration && state.isRecording) {
      actions.stopRecording();
    }
  }, [state.duration, maxDuration, state.isRecording, actions]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSend = () => {
    if (state.audioBlob) {
      onRecordingComplete(state.audioBlob, state.duration);
      actions.clearRecording();
    }
  };

  return (
    <div className="relative w-full">
      <AnimatePresence mode="wait">
        {/* Start Recording Button */}
        {!state.isRecording && !state.audioBlob && (
          <motion.div
            key="start"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="space-y-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={actions.startRecording}
              disabled={disabled}
              className={cn(
                'w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl',
                'bg-gradient-to-r from-accent/10 to-accent/5 hover:from-accent/20 hover:to-accent/10',
                'border-2 border-dashed border-accent/30 hover:border-accent/50',
                'transition-all duration-200',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="p-2.5 rounded-full bg-accent/20">
                <Mic className="w-5 h-5 text-accent" />
              </div>
              <div className="text-left">
                <span className="block font-medium text-accent">Tap to record voice message</span>
                <span className="block text-xs text-text-muted">Up to {formatDuration(maxDuration)} • Click and speak</span>
              </div>
            </motion.button>
          </motion.div>
        )}

        {/* Recording UI */}
        {state.isRecording && (
          <motion.div
            key="recording"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="space-y-3"
          >
            {/* Recording status banner */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
              <motion.div
                className="w-2 h-2 rounded-full bg-red-500"
                animate={{ opacity: state.isPaused ? 0.5 : [1, 0.5, 1] }}
                transition={{
                  duration: 1,
                  repeat: state.isPaused ? 0 : Infinity,
                }}
              />
              <span className="text-sm font-medium text-red-500">
                {state.isPaused ? 'Recording paused' : 'Recording...'}
              </span>
              <span className="text-xs text-red-400 ml-auto">
                Speak clearly into your microphone
              </span>
            </div>

            {/* Main recording panel */}
            <div className="flex items-center gap-4 px-4 py-4 rounded-xl bg-bg-tertiary border border-border">
              {/* Animated waveform */}
              <div className="flex items-center gap-0.5 h-10 flex-1">
                {Array.from({ length: 32 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 bg-red-500 rounded-full"
                    animate={{
                      height: state.isPaused
                        ? 4
                        : [4, Math.random() * 28 + 8, 4],
                    }}
                    transition={{
                      duration: 0.4,
                      repeat: state.isPaused ? 0 : Infinity,
                      delay: i * 0.02,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>

              {/* Duration */}
              <div className="flex flex-col items-end">
                <span className="font-mono text-xl font-bold text-red-500">
                  {formatDuration(state.duration)}
                </span>
                <span className="text-xs text-text-muted">
                  max {formatDuration(maxDuration)}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={
                  state.isPaused ? actions.resumeRecording : actions.pauseRecording
                }
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-tertiary hover:bg-bg-secondary border border-border transition-colors"
              >
                {state.isPaused ? (
                  <>
                    <Play className="w-4 h-4 text-accent" />
                    <span className="text-sm font-medium">Resume</span>
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium">Pause</span>
                  </>
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={actions.stopRecording}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                <Square className="w-4 h-4" />
                <span className="text-sm font-medium">Stop & Review</span>
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Preview UI */}
        {state.audioBlob && !state.isRecording && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="space-y-3"
          >
            {/* Preview banner */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/20">
              <div className="w-2 h-2 rounded-full bg-accent" />
              <span className="text-sm font-medium text-accent">Voice message ready</span>
              <span className="text-xs text-text-muted ml-auto">
                Listen to preview before sending
              </span>
            </div>

            {/* Audio player */}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-bg-tertiary border border-border">
              <audio
                src={state.audioUrl!}
                controls
                className="flex-1 h-10"
              />
              <span className="text-sm font-mono font-medium text-accent px-2 py-1 rounded-lg bg-accent/10">
                {formatDuration(state.duration)}
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={actions.clearRecording}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl hover:bg-red-500/10 text-text-muted hover:text-red-500 border border-border hover:border-red-500/30 transition-all"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm font-medium">Discard</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSend}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-accent hover:bg-accent-light text-white transition-colors shadow-lg shadow-accent/25"
              >
                <Send className="w-4 h-4" />
                <span className="text-sm font-medium">Send Voice Message</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      {state.error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
        >
          <span className="text-red-500 text-sm">{state.error}</span>
        </motion.div>
      )}
    </div>
  );
}
